'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { BrandWordmark } from '@/components/common/BrandWordmark';
import { buildLocalizedSeoPath, type SeoLocale } from '@/lib/seo/site';
import { SUPPORT_EMAIL } from '@/lib/legal/merchant';
import { PUBLIC_COPYRIGHT_YEAR } from '@/lib/seo/copyright-year';
import styles from './PublicContent.module.css';

/** Content footer uses the existing homepage copy, including in static Pages Router guides. */
export function PublicContentFooter({ locale }: { locale: SeoLocale }) {
  const t = useTranslations('auth.cinematic.mobileGrowth.footer');
  const localize = (path: string) => buildLocalizedSeoPath(path, locale);
  const columns = [
    { title: t('sections.create'), links: [
      { label: t('features.videoRecreation'), path: '/ai-video-recreation' },
      { label: t('features.productPhotography'), path: '/ai-product-photography' },
      { label: t('features.fashionModels'), path: '/ai-fashion-model-generator' },
      { label: t('features.lookbooks'), path: '/ai-lookbook-generator' },
      { label: t('features.virtualTryOn'), path: '/virtual-try-on' },
    ] },
    { title: t('sections.learn'), links: [
      { label: t('guides'), path: '/guides' },
      { label: t('features.referenceWorkflow'), path: '/guides/reference-image-ai-generation-workflow' },
      { label: t('about'), path: '/about' },
    ] },
    { title: t('sections.business'), links: [
      { label: t('pricing'), path: '/pricing' },
      { label: t('support'), path: '/support' },
      { label: t('contact'), path: '/#contact' },
    ] },
  ];
  return <footer className={styles.footer}>
    <div className={styles.footerInner}>
      <div className={styles.footerGrid}>
        <div className={styles.footerBrand}>
          <Link href={localize('/')} aria-label="DOBIDOBI"><BrandWordmark alt="DOBIDOBI" className="relative block h-9 w-[170px]" sizes="170px" /></Link>
          <p>{t('tagline')}</p>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </div>
        {columns.map((column) => <nav key={column.title} aria-label={column.title}>
          <h2>{column.title}</h2><ul>{column.links.map((link) => <li key={link.path}><Link href={localize(link.path)}>{link.label}</Link></li>)}</ul>
        </nav>)}
      </div>
      <div className={styles.footerBottom}>
        <p>{t('copyright', { year: PUBLIC_COPYRIGHT_YEAR })}</p>
        <nav aria-label={t('legalLabel')}>
          <Link href={localize('/cookie-notice')}>{t('cookieNotice')}</Link>
          <Link href={localize('/terms')}>{t('terms')}</Link>
          <Link href={localize('/privacy')}>{t('privacy')}</Link>
        </nav>
      </div>
    </div>
  </footer>;
}
